import { useState } from 'react';
import {
  Color, Size, defaultLookupAnswers, fill, formatCentsAmount, lookupRowPath, lookupRunActPath, lookupRunFilePath, lookupRunPath, lookupStepPath, t,
  type LookupLineAnswer, type LookupRunView, type LookupStepLine, type LookupStepState, type LookupStepView, type StringKey,
} from '@bms2/shared';
import { Button, Checkbox, Pill, TextInput } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import { useFetchedBlob } from '../../components/files/useFetchedBlob';
import { apiFetch, post, useApi } from './api';

/** How long a document opened in a tab keeps its blob address, long enough for the tab to read it. */
const BLOB_TAB_MS = 60_000;
import styles from './LookupStepThrough.module.css';

const STATE_LABEL: Readonly<Record<LookupStepState, StringKey>> = {
  Waiting: 'itemLookup.state.waiting',
  Walked: 'itemLookup.state.walked',
  Accepted: 'itemLookup.state.accepted',
  Skipped: 'itemLookup.state.skipped',
};

/** 🔒 A step's state as its status group: accepted good, skipped inactive, walked and waiting a warning still to decide. */
const STATE_GROUP: Readonly<Record<LookupStepState, 'good' | 'inactive' | 'warning'>> = { Waiting: 'warning', Walked: 'warning', Accepted: 'good', Skipped: 'inactive' };

/** A value as the line draws it: the price through the one money formatter, the rest as read. */
function shown(line: LookupStepLine, value: string): string {
  if (!line.money) return value;
  const amount = Number(value);
  // The catalog's list price is in dollars; the formatter reads whole cents.
  return Number.isFinite(amount) ? formatCentsAmount(Math.round(amount * 100)) : value;
}

/** A picture of the bundle, drawn from a blob the browser made (decision 0549). */
function PicturePreview({ path, label }: { path: string; label: string }) {
  const blob = useFetchedBlob(path);
  if (blob.kind !== 'drawn') return null;
  return <img className={styles.picture} src={blob.url} alt={label} />;
}

/** A document of the bundle, opened in a tab from a blob the browser made (decision 0549); the blob is revoked once the tab has it. */
function DocumentPreview({ path }: { path: string }) {
  const open = async () => {
    const response = await apiFetch(path);
    if (!response.ok || typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(await response.blob());
    window.open(url, '_blank', 'noopener');
    window.setTimeout(() => URL.revokeObjectURL(url), BLOB_TAB_MS);
  };
  return <Button size={Size.Small} text={[t('itemLookup.step.preview')]} actions={[() => void open()]} />;
}

/**
 * 🔒 The manufacturer lookup's Step Through (piece 16, *Manufacturer lookup*;
 * decisions 12660, 12661; T3814): decision 1483's walk over a lookup run, one
 * Item a step. Each line shows what was found beside what the Item holds; a
 * line is ticked only where the Item holds nothing, so a held value changes only
 * where the person ticks it. Next and Back save the step's answers, Accept writes
 * the ticked lines, Skip writes nothing, and Accept All opens only once every
 * step left has been walked. Close ends the run.
 */
export function LookupStepThrough({ runId, apiBaseUrl = '', onClosed, openerId = null }: { runId: string; apiBaseUrl?: string; onClosed: (wrote: boolean) => void; openerId?: string | null }) {
  const runRead = useApi<LookupRunView>(lookupRunPath(runId), apiBaseUrl);
  const run = runRead.state.kind === 'ready' ? runRead.state.data : null;
  const [index, setIndex] = useState(0);
  const current = run?.steps[index] ?? null;
  const stepRead = useApi<LookupStepView>(current ? lookupRowPath(runId, current.rowId) : null, apiBaseUrl);
  const step = stepRead.state.kind === 'ready' && stepRead.state.data.rowId === current?.rowId ? stepRead.state.data : null;
  const [drafts, setDrafts] = useState<Record<string, Record<string, LookupLineAnswer>>>({});
  const [refusal, setRefusal] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [wrote, setWrote] = useState(false);

  const open = run?.status === 'Staged' && step !== null && (step.state === 'Waiting' || step.state === 'Walked');
  const answers: Record<string, LookupLineAnswer> = step ? drafts[step.rowId] ?? { ...defaultLookupAnswers(step.lines), ...(step.answers ?? {}) } : {};
  const setAnswer = (key: string, next: LookupLineAnswer) => {
    if (!step) return;
    setDrafts((held) => ({ ...held, [step.rowId]: { ...answers, [key]: next } }));
  };
  const readAgain = () => {
    runRead.reload();
    stepRead.reload();
  };

  /** A write, then the run and the step read again, so what the server did is what shows. */
  const act = async (path: string, body: unknown): Promise<boolean> => {
    setBusy(true);
    setRefusal(null);
    const answer = await post(path, body, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) {
      setRefusal(answer.message);
      return false;
    }
    return true;
  };
  /** 🔒 Next and Back keep the step's answers as they stand, which is what makes the step walked (decision 12661). */
  const move = async (to: number) => {
    if (!run || to < 0 || to >= run.steps.length) return;
    if (open && step && !(await act(lookupStepPath(runId, step.rowId, 'walk'), { answers }))) return;
    setIndex(to);
    readAgain();
  };
  const nextOpen = (after: LookupRunView | null): number => {
    if (!after) return index;
    const later = after.steps.findIndex((one, at) => at > index && (one.state === 'Waiting' || one.state === 'Walked'));
    return later >= 0 ? later : Math.min(index + 1, after.steps.length - 1);
  };
  const decide = async (kind: 'accept' | 'skip') => {
    if (!step) return;
    if (!(await act(lookupStepPath(runId, step.rowId, kind), kind === 'accept' ? { answers } : {}))) return;
    if (kind === 'accept') setWrote(true);
    setIndex(nextOpen(run));
    readAgain();
  };
  const acceptAll = async () => {
    if (!(await act(lookupRunActPath(runId, 'accept-all'), {}))) return readAgain();
    setWrote(true);
    readAgain();
  };
  const close = async () => {
    if (run?.status === 'Staged') await act(lookupRunActPath(runId, 'close'), {});
    onClosed(wrote || (run?.steps.some((one) => one.state === 'Accepted') ?? false));
  };

  const count = run?.steps.length ?? 0;
  const undecided = run?.steps.filter((one) => one.state === 'Waiting' || one.state === 'Walked').length ?? 0;
  const done = run !== null && run.status !== 'Staged';
  return (
    <FormFrame
      host="window"
      open
      width="document"
      height="tall"
      title={t('itemLookup.step.title')}
      {...(run?.origin ? { subtitle: run.origin } : {})}
      onCancel={() => void close()}
      cancelLabel={t('itemLookup.step.close')}
      openerId={openerId}
      dataForm="lookup-step-through"
      // 🔒 Closing with steps left ends the run, so the frame asks first (decision 3061).
      typed={undecided > 0 && run?.status === 'Staged'}
      refusal={refusal ?? (step?.refusal ?? null)}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('itemLookup.step.accept'), onAct: () => void decide('accept'), disabled: !open || busy, ...(open ? {} : { disabledReason: t('itemLookup.step.decided') }) }}
    >
      <div className={styles.frame} data-lookup-step-through={runId}>
        {done ? (
          <p className={styles.hint} data-lookup-run-done="">
            {fill(t('itemLookup.run.done'), { accepted: run.steps.filter((one) => one.state === 'Accepted').length, skipped: run.steps.filter((one) => one.state === 'Skipped').length })}
          </p>
        ) : null}
        <div className={styles.bar}>
          <Button size={Size.Small} text={[t('itemLookup.step.back')]} actions={[() => void move(index - 1)]} disabled={index === 0 || busy} {...(index === 0 ? { disabledReason: t('itemLookup.step.backWhy') } : {})} />
          <span className={styles.position} data-lookup-position={index + 1}>{fill(t('itemLookup.step.of'), { n: count === 0 ? 0 : index + 1, count })}</span>
          <Button size={Size.Small} text={[t('itemLookup.step.next')]} actions={[() => void move(index + 1)]} disabled={index >= count - 1 || busy} {...(index >= count - 1 ? { disabledReason: t('itemLookup.step.nextWhy') } : {})} />
          {/* Skip writes nothing for this Item and moves on; it is no destructive act, so it stands with the step's moves and not in the footer's red slot. */}
          <Button size={Size.Small} text={[t('itemLookup.step.skip')]} actions={[() => void decide('skip')]} disabled={!open || busy} {...(open ? {} : { disabledReason: t('itemLookup.step.decided') })} />
          <span className={styles.push}>
            {/* 🔒 Accept All only for a group already walked (decision 12661): disabled, saying why, until every step left was saved by Next or Back. */}
            <Button
              size={Size.Small}
              color={Color.Navy}
              text={[t('itemLookup.step.acceptAll')]}
              actions={[() => void acceptAll()]}
              disabled={!run?.acceptAllReady || busy}
              {...(run?.acceptAllReady ? {} : { disabledReason: t('itemLookup.step.acceptAllWhy') })}
            />
          </span>
        </div>
        {current ? (
          <div className={styles.heading}>
            <strong data-lookup-step-label="">{current.label}</strong>
            <Pill text={t(STATE_LABEL[current.state])} foreground={`var(--status-${STATE_GROUP[current.state]}-text)`} background={`var(--status-${STATE_GROUP[current.state]}-bg)`} size={Size.Small} outlined />
          </div>
        ) : null}
        {step?.source ? <p className={styles.hint}>{t('itemLookup.step.source')} {step.source}</p> : null}
        <p className={styles.hint}>{t('itemLookup.step.hint')}</p>
        {step && step.lines.length === 0 ? <p className={styles.hint}>{t('itemLookup.step.nothingFound')}</p> : null}
        {step && step.lines.length > 0 ? (
          // A row per line, not a bare table: every table in the application is the List Viewer's, and a preview inside a window is a list.
          <ul className={styles.lines} data-lookup-lines={step.rowId}>
            <li className={styles.head} aria-hidden="true">
              <span>{t('itemLookup.step.take')}</span>
              <span>{t('itemLookup.step.found')}</span>
              <span>{t('itemLookup.step.held')}</span>
            </li>
            {step.lines.map((line) => {
              const answer = answers[line.key] ?? { take: false };
              const editing = open && line.editable && answer.take;
              return (
                <li key={line.key} className={styles.line} data-lookup-line={line.key} data-take={String(answer.take)} data-same={String(line.same)}>
                  <span className={styles.take}>
                    <Checkbox
                      label={fill(t('itemLookup.step.takeLine'), { label: line.label })}
                      labelHidden
                      checked={answer.take}
                      disabled={!open || line.same}
                      onChange={(take) => setAnswer(line.key, { ...answer, take })}
                    />
                  </span>
                  <span className={styles.found}>
                    <span className={styles.label}>
                      {line.label}
                      {line.newType ? <Pill text={t('itemLookup.step.newType')} foreground="var(--status-warning-text)" background="var(--status-warning-bg)" size={Size.Small} outlined /> : null}
                      {line.readBy === 'AI' ? <Pill text={t('itemLookup.step.readByAi')} foreground="var(--status-warning-text)" background="var(--status-warning-bg)" size={Size.Small} outlined /> : null}
                      {line.readBy === 'AI' && line.confidence !== null ? <span className={styles.muted}>{fill(t('itemLookup.step.confidence'), { percent: line.confidence })}</span> : null}
                    </span>
                    {editing ? (
                      <TextInput size={Size.Large} label={fill(t('itemLookup.step.value'), { label: line.label })} labelHidden value={answer.value ?? line.found} onChange={(value) => setAnswer(line.key, { ...answer, value })} />
                    ) : (
                      <span>{shown(line, answer.value ?? line.found)}</span>
                    )}
                    {line.kind === 'picture' && line.file ? <PicturePreview path={`${apiBaseUrl}${lookupRunFilePath(runId, line.file)}`} label={line.label} /> : null}
                    {line.kind === 'document' && line.file ? <DocumentPreview path={`${apiBaseUrl}${lookupRunFilePath(runId, line.file)}`} /> : null}
                    {line.refusal ? <span className={styles.refusal}>{line.refusal}</span> : null}
                  </span>
                  <span className={styles.held}>{line.same ? t('itemLookup.step.same') : line.held === null ? <span className={styles.muted}>{t('itemLookup.step.nothingHeld')}</span> : shown(line, line.held)}</span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </FormFrame>
  );
}
